import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import './ProviderJoinRequestTodo.scss';
import MiniCheckBox from 'src/components/modules/MiniCheckBox/MiniCheckBox';
import AppButton from 'src/components/base/Button/Button';
import { Typography } from 'src/latest/src/components';
import { Todo } from 'src/latest/src/types';
import { TodoTypeEnums } from 'src/latest/src/constants';
import { clearModalContent } from 'src/redux/modules/modalEvent';
import { addFlashNotification } from 'src/redux/modules/flashNotification';
import { updateIfollowedUpTodo } from 'src/redux/services/loggingServices';
import { changeTodoStatusV1 } from 'src/api/user';
import { Logger } from 'src/latest/src/services';
import {
  CloseWithoutInvitingReason,
  ProviderJoinRequestAction,
} from 'src/latest/src/components/Todo/ProviderJoinRequestTodo/ProviderJoinRequest';

interface ProviderJoinRequestMetadata {
  first_name?: string;
  last_name?: string;
  professional_email?: string;
  phone_number?: string;
  practice_role?: string;
  practice_or_clinic_name?: string;
  number_of_providers?: string;
  current_ehr?: string;
  states_of_licensure?: string[];
  how_did_you_hear_about_us?: string;
}

interface ProviderJoinRequestTodoProps {
  todoId: string;
  todo: Todo & { metadata: ProviderJoinRequestMetadata };
  getUpdatedAuditLogs: () => void;
}

interface DetailField {
  key: string;
  label: string;
  value?: string;
  chips?: string[];
  fullWidth?: boolean;
  highlight?: boolean;
}

const formatEnumLabel = (value?: string): string => {
  if (!value) return '';

  return value
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getDetailFields = (
  metadata: ProviderJoinRequestMetadata = {},
): DetailField[] => {
  const fields: DetailField[] = [
    {
      key: 'professional_email',
      label: 'Professional Email',
      value: metadata.professional_email,
    },
    {
      key: 'phone_number',
      label: 'Phone Number',
      value: metadata.phone_number,
    },
    {
      key: 'practice_role',
      label: 'Practice Role',
      value: formatEnumLabel(metadata.practice_role),
    },
    {
      key: 'practice_or_clinic_name',
      label: 'Practice/Clinic Name',
      value: metadata.practice_or_clinic_name,
    },
    {
      key: 'number_of_providers',
      label: 'Number of Providers',
      value: metadata.number_of_providers,
    },
    {
      key: 'current_ehr',
      label: 'Current EHR',
      value: metadata.current_ehr,
    },
    {
      key: 'states_of_licensure',
      label: 'States of Licensure',
      chips: Array.isArray(metadata.states_of_licensure)
        ? metadata.states_of_licensure
        : undefined,
      fullWidth: true,
    },
    {
      key: 'how_did_you_hear_about_us',
      label: 'How Did You Hear About Us',
      value: metadata.how_did_you_hear_about_us,
      fullWidth: true,
    },
  ];

  return fields.filter(
    field =>
      (field.chips && field.chips.length > 0) ||
      (!!field.value && field.value.trim() !== ''),
  );
};

const ProviderJoinRequestTodo: React.FC<ProviderJoinRequestTodoProps> = ({
  todoId,
  todo,
  getUpdatedAuditLogs,
}) => {
  const [primaryOption, setPrimaryOption] = useState<
    ProviderJoinRequestAction | ''
  >('');
  const [secondaryOption, setSecondaryOption] = useState<
    CloseWithoutInvitingReason | ''
  >('');
  const [note, setNote] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const dispatch = useDispatch();

  const isPracticeOwnerRequest =
    todo?.type === TodoTypeEnums.PRACTICE_OWNER_JOIN_REQUEST_TODO;

  const requesterName =
    [todo?.metadata?.first_name, todo?.metadata?.last_name]
      .filter(Boolean)
      .join(' ') || todo?.user_full_name;

  const detailFields = useMemo(() => {
    const fields = getDetailFields(todo?.metadata);

    if (!requesterName) return fields;

    const nameField: DetailField = {
      key: 'requester_name',
      label: isPracticeOwnerRequest ? 'Practice Owner Name' : 'Provider Name',
      value: requesterName,
      fullWidth: true,
      highlight: true,
    };

    return [nameField, ...fields];
  }, [todo?.metadata, requesterName, isPracticeOwnerRequest]);

  useEffect(() => {
    if (primaryOption !== ProviderJoinRequestAction.CLOSE_WITHOUT_INVITING) {
      setSecondaryOption('');
    }

    if (primaryOption !== ProviderJoinRequestAction.MARK_AS_PENDING) {
      setNote('');
    }
  }, [primaryOption]);

  const canSave =
    primaryOption === ProviderJoinRequestAction.CLOSE_WITHOUT_INVITING
      ? !!secondaryOption
      : !!primaryOption;

  const editNoteContent = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e?.target?.value);
  };

  const selectPrimaryOption = (option: ProviderJoinRequestAction) => {
    if (isSaving) return;
    setPrimaryOption(option);
  };

  const selectSecondaryOption = (reason: CloseWithoutInvitingReason) => {
    if (isSaving) return;
    setSecondaryOption(reason);
  };

  const inviteProvider = async (): Promise<void> => {
    await updateIfollowedUpTodo(todoId, {
      action: ProviderJoinRequestAction.INVITE_PROVIDER,
    });
  };

  const markTodoAsPending = async (): Promise<void> => {
    await changeTodoStatusV1(todoId, false, note);
  };

  const closeWithoutInviting = async (
    reason: CloseWithoutInvitingReason,
  ): Promise<void> => {
    await updateIfollowedUpTodo(todoId, {
      action: ProviderJoinRequestAction.CLOSE_WITHOUT_INVITING,
      close_reason: reason,
    });
  };

  const handleSave = async () => {
    if (!canSave || isSaving) return;

    setErrorMessage('');
    setIsSaving(true);

    try {
      switch (primaryOption) {
        case ProviderJoinRequestAction.INVITE_PROVIDER:
          await inviteProvider();
          break;

        case ProviderJoinRequestAction.MARK_AS_PENDING:
          await markTodoAsPending();
          break;

        case ProviderJoinRequestAction.CLOSE_WITHOUT_INVITING:
          if (!secondaryOption) {
            throw new Error('Please select a reason before saving.');
          }

          await closeWithoutInviting(secondaryOption);
          break;

        default:
          return;
      }

      dispatch(clearModalContent());
      getUpdatedAuditLogs();
    } catch (e: any) {
      const message =
        e?.response?.data?.message ||
        e?.message ||
        'Something went wrong while saving this TO-DO. Please try again.';

      setErrorMessage(message);
      dispatch(
        addFlashNotification({
          message,
          isError: true,
          dedupeKey: `provider_join_request_todo_error_${todoId}`,
        }),
      );
      Logger.error('ProviderJoinRequestTodo save error', e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    dispatch(clearModalContent());
  };

  return (
    <div className="provider-join-request-todo-container">
      {detailFields.length > 0 ? (
        <div className="provider-join-request-todo-container__details">
          {detailFields.map(field => (
            <div
              className={[
                'provider-join-request-todo-container__field',
                field.fullWidth &&
                  'provider-join-request-todo-container__field--full',
                field.highlight &&
                  'provider-join-request-todo-container__field--highlight',
              ]
                .filter(Boolean)
                .join(' ')}
              key={field.key}
            >
              <span className="provider-join-request-todo-container__field-label">
                {field.label}
              </span>
              {field.chips ? (
                <div className="provider-join-request-todo-container__chip-row">
                  {field.chips.map(chip => (
                    <span
                      className="provider-join-request-todo-container__chip"
                      key={chip}
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="provider-join-request-todo-container__field-value">
                  {field.value}
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <Typography
          variant="size-14"
          color="color-19"
          className="provider-join-request-todo-container__no-details"
        >
          No additional information was submitted with this request.
        </Typography>
      )}

      <div className="provider-join-request-todo-container__primary-options">
        <MiniCheckBox
          text="Invite Provider"
          isCheckFirst={true}
          isRadio={true}
          isActive={primaryOption === ProviderJoinRequestAction.INVITE_PROVIDER}
          onClick={() =>
            selectPrimaryOption(ProviderJoinRequestAction.INVITE_PROVIDER)
          }
          value={primaryOption}
        />

        <MiniCheckBox
          text="Mark TODO as Pending"
          isCheckFirst={true}
          isRadio={true}
          isActive={primaryOption === ProviderJoinRequestAction.MARK_AS_PENDING}
          onClick={() =>
            selectPrimaryOption(ProviderJoinRequestAction.MARK_AS_PENDING)
          }
          value={primaryOption}
        />

        <MiniCheckBox
          text="Close without Inviting"
          isCheckFirst={true}
          isRadio={true}
          isActive={
            primaryOption === ProviderJoinRequestAction.CLOSE_WITHOUT_INVITING
          }
          onClick={() =>
            selectPrimaryOption(
              ProviderJoinRequestAction.CLOSE_WITHOUT_INVITING,
            )
          }
          value={primaryOption}
        />
      </div>

      {primaryOption === ProviderJoinRequestAction.MARK_AS_PENDING && (
        <div className="provider-join-request-todo-container__note">
          <Typography
            component="span"
            variant="size-14"
            weight="semi-bold"
            className="me-2"
          >
            Note
          </Typography>
          <Typography component="span" variant="size-14" color="color-28">
            (Optional)
          </Typography>
          <textarea
            rows={4}
            maxLength={300}
            placeholder="Add Note"
            value={note}
            onChange={editNoteContent}
            disabled={isSaving}
          />
        </div>
      )}

      {primaryOption === ProviderJoinRequestAction.CLOSE_WITHOUT_INVITING && (
        <div className="provider-join-request-todo-container__secondary-options">
          <MiniCheckBox
            text="Provider will be Invited with Practice"
            isCheckFirst={true}
            isRadio={true}
            isActive={
              secondaryOption ===
              CloseWithoutInvitingReason.PROVIDER_WILL_BE_INVITED_WITH_PRACTICE
            }
            onClick={() =>
              selectSecondaryOption(
                CloseWithoutInvitingReason.PROVIDER_WILL_BE_INVITED_WITH_PRACTICE,
              )
            }
            value={secondaryOption}
          />

          <MiniCheckBox
            text="Provider not Suitable"
            isCheckFirst={true}
            isRadio={true}
            isActive={
              secondaryOption ===
              CloseWithoutInvitingReason.PROVIDER_NOT_SUITABLE
            }
            onClick={() =>
              selectSecondaryOption(
                CloseWithoutInvitingReason.PROVIDER_NOT_SUITABLE,
              )
            }
            value={secondaryOption}
          />
        </div>
      )}

      {errorMessage && (
        <Typography
          variant="size-14"
          color="color-6"
          weight="regular"
          className="provider-join-request-todo-container__error"
        >
          {errorMessage}
        </Typography>
      )}

      <div className="provider-join-request-todo-container__actions">
        <AppButton
          label="Close"
          variant="light"
          textWeight="bold"
          textSize="size-14"
          disabled={isSaving}
          className="provider-join-request-todo-container__close-button bg-white"
          onClick={handleClose}
        />

        <AppButton
          label="Save"
          variant="primary"
          textWeight="bold"
          textSize="size-14"
          isProcessing={isSaving}
          disabled={!canSave || isSaving}
          className="provider-join-request-todo-container__save-button"
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default ProviderJoinRequestTodo;
